import {
	AdminSearchContainer,
	AdminSearchInputLine,
	AdminSearchTextField,
	AdminSettingLandingPageContent,
	AdminSettingLandingPageHeader,
	SearchParagraphStyled
} from "./AdminSettingLandingPage.styles.ts";
import AdminSearchTable from "components/AdminSettings/AdminSearchTable.tsx";
import useSession from "hooks/useSession.ts";
import { AdminSettingsCacheKeys } from "./adminSettingConstants.ts";
import { ChangeEvent, useCallback, useContext, useMemo, useState } from "react";
import TableSkeleton from "components/Common/Table/TableSkeleton/TableSkeleton.tsx";
import AdminManageSettings from "components/AdminSettings/AdminManageSettings.tsx";
import { useLazyGetAdminSearchResultsQuery } from "features/api/permissionsApi.ts";
import { AdminSearchResult, formatSearchResults } from "./utils/adminSettingsUtils.ts";
import { useGetUserDetailsQuery } from "features/api/userApi.ts";
import { Button, InputAdornment, Skeleton } from "@mui/material";
import {
	AccountAdminHeaderText,
	AdminSearchLabelText,
	AdminSearchText,
	minCharCountText,
	searchHeaderButtonText
} from "constants/text.ts";
import CloseIcon from "@mui/icons-material/Close";
import ConfigurationsContext from "context/configurations/ConfigurationsContext.ts";
import APIErrorCustomBanner from "components/Common/APIErrorCustomBanner/APIErrorCustomBanner.tsx";
import { UserPermission } from "types/declarations/enums.ts";

const MIN_SEARCH_LENGTH = 3;

const AdminSettingsPage = () => {
	const { configurationsData } = useContext(ConfigurationsContext);
	const [searchResponse, setSearchResponse] = useSession<AdminSearchResult[] | undefined>(
		AdminSettingsCacheKeys.AdminSearchResponse,
		undefined
	);
	const [hasInvokedSearch, setHasInvokedSearch] = useSession<boolean>(
		AdminSettingsCacheKeys.AdminSearchInvoked,
		false
	);
	const [currentSearch, setCurrentSearch] = useSession<string>(AdminSettingsCacheKeys.AdminSearch, "");
	const [hasSearchValidationError, setHasSearchValidationError] = useState(false);

	const [triggerSearch, { isFetching: isSearchLoading, error }] = useLazyGetAdminSearchResultsQuery();
	const { data: userDetails, isLoading: isGetUserDetailsLoading } = useGetUserDetailsQuery();

	const isAdmin = useMemo(
		() => userDetails?.permissions?.includes(UserPermission.ADMIN) ?? false,
		[userDetails?.permissions]
	);
	const isUserPreferenceEnabled = configurationsData?.featureFlags?.userPreferenceEnabled ?? false;

	const searchLength = currentSearch?.trim().length ?? 0;
	const isSearchValid = searchLength >= MIN_SEARCH_LENGTH;
	const showClearIcon = currentSearch?.length >= MIN_SEARCH_LENGTH;

	const invokeSearch = useCallback(async () => {
		if (!isSearchValid) {
			setHasSearchValidationError(true);
			return;
		}

		try {
			setHasInvokedSearch(true);
			setHasSearchValidationError(false);
			await Promise.resolve();
			const response = await triggerSearch(currentSearch).unwrap();
			const searchResults = formatSearchResults(response);
			setSearchResponse(searchResults);
		} catch (err) {
			console.error(err);
		}
	}, [currentSearch, isSearchValid, setSearchResponse, triggerSearch, setHasInvokedSearch]);

	const resetSearch = useCallback(() => {
		setCurrentSearch("");
		setSearchResponse(undefined);
		setHasInvokedSearch(false);
		setHasSearchValidationError(false);
	}, [setCurrentSearch, setSearchResponse, setHasInvokedSearch]);

	const handleSearchFieldOnChange = useCallback(
		(e: ChangeEvent<HTMLInputElement>) => {
			const newValue = e.target.value;
			setCurrentSearch(newValue);

			if (newValue.length === 0 && (searchResponse?.length ?? 0) > 0) {
				resetSearch();
			}

			if (hasSearchValidationError) {
				setHasSearchValidationError(false);
			}
		},
		[resetSearch, searchResponse, setCurrentSearch, hasSearchValidationError]
	);

	const handleSearchFieldOnKeyDown = useCallback(
		(e: any) => {
			if (e.key === "Enter") {
				invokeSearch().catch(() => {});
			}
		},
		[invokeSearch]
	);

	const renderContent = () => {
		if (isSearchLoading) {
			return (
				<AdminSearchContainer>
					<TableSkeleton key="admin-search-skeleton" />
				</AdminSearchContainer>
			);
		}

		if (searchResponse && hasInvokedSearch) {
			return (
				<AdminSearchContainer>
					<AdminSearchTable requests={searchResponse} />
				</AdminSearchContainer>
			);
		}

		return <AdminManageSettings />;
	};

	if (!isUserPreferenceEnabled || !isAdmin) {
		return (
			<div>
				<APIErrorCustomBanner statusCode={403} />
			</div>
		);
	}

	if (error && (error as any)?.status) {
		return (
			<div data-testid="admin-settings-page">
				<APIErrorCustomBanner statusCode={(error as any)?.status} />
			</div>
		);
	}

	if (isGetUserDetailsLoading) {
		return <Skeleton variant="rectangular" />;
	}

	return (
		<>
			<AdminSettingLandingPageHeader>
				<h1>{AccountAdminHeaderText}</h1>
			</AdminSettingLandingPageHeader>

			<AdminSettingLandingPageContent>
				<AdminSearchContainer
					id="admin-search"
					data-testid="search-admin-accounts"
				>
					<SearchParagraphStyled>{AdminSearchText}</SearchParagraphStyled>
					<AdminSearchInputLine>
						<AdminSearchTextField
							size="small"
							required
							error={hasSearchValidationError}
							label={AdminSearchLabelText}
							helperText={hasSearchValidationError && minCharCountText}
							data-testid="search-header-input-field"
							value={currentSearch}
							onChange={handleSearchFieldOnChange}
							onKeyDown={handleSearchFieldOnKeyDown}
							slotProps={{
								input: {
									endAdornment: showClearIcon && (
										<InputAdornment
											position="end"
											onClick={resetSearch}
											sx={{ cursor: "pointer" }}
										>
											<CloseIcon cursor="pointer" />
										</InputAdornment>
									)
								},
								inputLabel: {
									required: false,
									shrink: true
								}
							}}
						/>
						<Button
							variant="contained"
							data-testid="search-header-button"
							data-id="search-header-button"
							onClick={() => {
								invokeSearch().catch(() => {});
							}}
							aria-label="Search Orders"
							size="small"
						>
							{searchHeaderButtonText}
						</Button>
					</AdminSearchInputLine>
				</AdminSearchContainer>
				{renderContent()}
			</AdminSettingLandingPageContent>
		</>
	);
};

export default AdminSettingsPage;
